import { Check } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoosePlanButton } from '../FeatureComparison/ChoosePlanButton';
import {
  ENTERPRISE_FROM_PRICE,
  PRICING_CHAIN,
  PricingPlan,
  getPlanRelation,
} from './pricingPlans.config';
import { cn } from '@/Domain/utils/cn';
import { StripeData } from '@/UI/Subscription/Subscription.page';

// FAQ deep-link targets, tried in this order. The match phrases are localized
// per language in `pricing.faq_links`, so the links work in every locale
// (mirrors quickchat-www). Longest phrase within a target wins.
const FAQ_LINK_ORDER = ['ai_credits', 'user_licenses', 'kb_articles', 'ai_models', 'resolution'];

type FaqMatcher = { target: string; needle: string; length: number };

const buildFaqMatchers = (faqLinks: Record<string, string[]>): FaqMatcher[] =>
  FAQ_LINK_ORDER.flatMap((target) =>
    [...(faqLinks[target] ?? [])]
      .sort((a, b) => b.length - a.length)
      .map((phrase) => ({ target, needle: phrase.toLowerCase(), length: phrase.length })),
  );

// Wraps the first matching phrase (case-insensitive) in an inline link that opens
// the matching FAQ item; the matched text keeps its original casing.
const renderFeature = (
  feature: string,
  matchers: FaqMatcher[],
  onFaqLink: (faqKey: string) => void,
): ReactNode => {
  const haystack = feature.toLowerCase();
  for (const { target, needle, length } of matchers) {
    const i = haystack.indexOf(needle);
    if (i === -1) continue;
    return (
      <>
        {feature.slice(0, i)}
        <span
          role="button"
          tabIndex={0}
          onClick={() => onFaqLink(target)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onFaqLink(target);
            }
          }}
          className="cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {feature.slice(i, i + length)}
        </span>
        {feature.slice(i + length)}
      </>
    );
  }
  return feature;
};

type Props = {
  plan: PricingPlan;
  isBillingYearly: boolean;
  stripeData: StripeData;
  onFaqLink: (faqKey: string) => void;
};

export const PricingPlanCard = ({ plan, isBillingYearly, stripeData, onFaqLink }: Props) => {
  const { t } = useTranslation('subscription_page');

  const featuresFor = (key: string): string[] =>
    t(`pricing.plans.${key}.features`, { returnObjects: true }) as unknown as string[];

  const faqMatchers = buildFaqMatchers(
    t('pricing.faq_links', { returnObjects: true }) as unknown as Record<string, string[]>,
  );

  const relation = getPlanRelation(
    plan,
    stripeData.currentPlan,
    stripeData.subscription,
    isBillingYearly,
  );

  const name = t(`tiers.${plan.key}`);
  const ctaLabel =
    relation === 'current'
      ? t('button.current_plan')
      : relation === 'upcoming'
        ? t('button.upcoming_plan')
        : relation === 'downgrade'
          ? t('pricing.cta.downgrade')
          : relation === 'update'
            ? t('pricing.cta.update')
            : plan.isCustom
              ? t('button.contact_us')
              : t('pricing.cta.upgrade');
  const disabled = relation === 'current' || relation === 'upcoming';
  const ctaVariant = relation === 'next' ? 'dark' : 'secondary';

  const chainIndex = PRICING_CHAIN.indexOf(plan.key);
  const previousKey = !plan.isCustom && chainIndex > 0 ? PRICING_CHAIN[chainIndex - 1] : null;
  const previousFeatures = previousKey ? featuresFor(previousKey) : [];
  const displayedFeatures = plan.isCustom
    ? featuresFor(plan.key)
    : featuresFor(plan.key).filter((feature) => !previousFeatures.includes(feature));
  const inheritsFrom = previousKey ? t(`tiers.${previousKey}`) : null;

  const price = isBillingYearly ? plan.monthlyPrice * 10 : plan.monthlyPrice;
  const priceSuffix = isBillingYearly ? t('pricing.per_year') : t('pricing.per_month');
  const showSavings = isBillingYearly && plan.monthlyPrice > 0;

  return (
    <div
      data-testid={`pricing-plan-${plan.key}`}
      className={cn(
        'flex flex-col rounded-xl border border-border bg-card p-6',
        plan.isCustom && 'sm:col-span-2 lg:col-span-2',
      )}
    >
      <h3 className="mb-4 font-semibold leading-none tracking-tight">{name}</h3>

      <div className="mb-5">
        {plan.isCustom ? (
          <p>
            <span className="text-sm text-muted-foreground">{t('pricing.from')} </span>
            <span className="text-2xl font-semibold text-foreground">${ENTERPRISE_FROM_PRICE}</span>
            <span className="text-sm text-muted-foreground">
              {renderFeature(t('pricing.per_resolution'), faqMatchers, onFaqLink)}
            </span>
          </p>
        ) : (
          <p>
            <span className="text-2xl font-semibold text-foreground">${price}</span>
            <span className="text-sm text-muted-foreground">{priceSuffix}</span>
          </p>
        )}
        {showSavings ? (
          <p className="mt-1 text-sm text-success" data-testid={`pricing-savings-${plan.key}`}>
            {t('pricing.save')} ${plan.monthlyPrice * 2}
          </p>
        ) : (
          <p className="mt-1 text-sm" aria-hidden="true">
            &nbsp;
          </p>
        )}
      </div>

      <div className="mb-6" data-testid={`pricing-cta-${plan.key}`}>
        <ChoosePlanButton
          tier={plan.tier}
          isBillingYearly={isBillingYearly}
          stripeData={stripeData}
          label={ctaLabel}
          variant={ctaVariant}
          disabled={disabled}
          isDowngrade={relation === 'downgrade'}
        />
      </div>

      {inheritsFrom && (
        <p className="mb-2.5 text-sm font-medium">
          {t('pricing.everything_in', { plan: inheritsFrom })}
        </p>
      )}
      <ul className="flex flex-col gap-2.5 text-sm text-muted-foreground">
        {displayedFeatures.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="mt-0.5 size-4 shrink-0" />
            <span>{renderFeature(feature, faqMatchers, onFaqLink)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
